import type {
  ICheckEmailVerifiedResponse,
  IClaimBonusesResponse,
  ICreateUserResponse,
  IEmailAvailableResponse,
  IGenericAPIResponse,
  IPasswordResetResponse,
  IProfileResponse,
  IResendVerificationResponse,
  ISetLoggedInResponse,
  IUpdateUserProfileResponse,
  IUserNameResponse,
} from '#api';
import type { IOnlineEventData } from '#shared/types/firebase.types';
import type { IBalanceData, ICreateUserData, IProfileData, TUserCurrency } from '#shared/types/user.types';
import type { TVipSocketMessage } from '~/store/vip';
import type { TWelcomeBonusMessage } from '~/store/welcome';
import type { ISocketOptions } from '~/utils/services/socket.service';
import { LazyModalGeneric, LazyModalSignUp } from '#components';
import { formatBalance } from '#shared/formatters/currency';
import { EKycResult, EKycStatus } from '#shared/types/account.types';
import { EBankId, EUserCurrency } from '#shared/types/user.types';
import { COOKIE_SESSION, LS_DISPLAY_NAME_PROMPT } from '~~/shared/const/cookies';
import { useAppToast } from '~/composables/shared/useAppToast';
import { useCashierLoader } from '~/composables/useCashierLoader';
import { useFriendsStoreLoader } from '~/composables/useFriendsStoreLoader';
import { useTypedNuxtApp } from '~/composables/useTypedNuxtApp';
import { useVipFeature } from '~/composables/useVipFeature';
import { stores } from '~/store/index';
import SupportChat, { ZENDESK_LO_USER_ID } from '~/utils/services/support-chat.service';

type TJsonBalanceData = {
  [K in keyof IBalanceData]: string;
};

export const useUserStore = defineStore('user', () => {
  const { showErrorToast } = useAppToast();

  const isLoggedIn = ref(false);
  const idTokenCookie = useCookie('idToken');
  const loadingQueryParams = useLoadingQueryParams();

  const route = useRoute();
  const loggedInState = useState<boolean>('loggedInState');

  async function getIdToken() {
    if (import.meta.server) return null;

    const auth = useTypedNuxtApp().$firebase.auth;
    const user: any = auth?.currentUser;

    if (user !== null && 'getIdToken' in user) {
      const token = await user.getIdToken();
      idTokenCookie.value = token;
      return token;
    }

    return idTokenCookie.value;
  }

  const socialSignUpIsPending = ref(false);
  const socialSignInIsPending = ref(false);
  const isCurrentUserDataFetching = ref(false);

  function setSocialSignUpIsPending(isPending: boolean) {
    socialSignUpIsPending.value = isPending;
  }

  function setSocialSignInIsPending(isPending: boolean) {
    socialSignInIsPending.value = isPending;
  }

  let zendeskTimeoutId: NodeJS.Timeout;

  function initLoggedOutZendeskChat() {
    zendeskTimeoutId = setTimeout(() => {
      if (stores.modals.hasAnyOpen()) return;
      SupportChat.init(ZENDESK_LO_USER_ID).then();
    }, 2000);
  }

  async function continueLoggingInUser() {
    await processUserLogin();
    await initWebsocket();
    await handleEmailVerifiedChecking();
    handleDisplayNamePrompt();

    stores.firebase.setInfoConnectedListener();
  }

  async function handleDisplayNamePrompt() {
    const localStorageFlag = localStorage.getItem(`${LS_DISPLAY_NAME_PROMPT}${getUserId()}`);

    if (localStorageFlag) {
      stores.signUp.setSignUpStep(ESignUpSteps.DisplayNamePrompt);
      await stores.modals.open('sign-up', LazyModalSignUp);
    }
  }

  onMounted(async () => {
    const auth = useTypedNuxtApp().$firebase.auth;
    const datadogRum = useTypedNuxtApp().$datadogRum;

    auth?.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        SupportChat.hideButton();

        if (socialSignUpIsPending?.value) return;

        await continueLoggingInUser();
      } else {
        if (idTokenCookie.value) {
          storeLogoutActions();
          destroyWebsocket();
          clearUser();
        }

        datadogRum?.clearUser();

        if (route.query?.playme) {
          if (route.query.source) {
            setSourceParam(route.query.source as string);
          }

          setPartnerId(route.query.playme as string);

          stores.modals.showSignUpModal();
        }

        if (!route.meta.availableForGuests) {
          navigateTo({ name: ERouteName.LOGGED_OUT });
        }

        initLoggedOutZendeskChat();

        loadingQueryParams.checkModalsOpeners();
        loadingQueryParams.handleUTMParams();
        loadingQueryParams.callTrafficTracker();
      }
    });

    watch(
      loggedInState,
      async (newValue) => {
        if (typeof newValue === 'undefined' || newValue === null) return;
        isLoggedIn.value = newValue;
      },
      { immediate: true },
    );
  });

  onBeforeUnmount(() => {
    clearTimeout(zendeskTimeoutId);
  });

  function storeLogoutActions() {
    stores.firebase.stopAlertSystemListeners();
  }

  async function handleEmailVerifiedChecking() {
    const user: any = useTypedNuxtApp().$firebase.auth?.currentUser;
    if (!user) return;

    if (!user.emailVerified && !stores.signUp.signUpInProcess) {
      const { success } = await resendVerificationCode();

      if (success) {
        stores.modals.close('sign-in');
        stores.signUp.setSignUpStep(ESignUpSteps.EmailConfirmation);
        await stores.modals.open('sign-up', LazyModalSignUp);
      } else {
        await stores.signUp.handleSignOut();
      }
    }
  }

  // MARK: partner id
  const partnerId = ref<string>();

  function setPartnerId(id: string) {
    partnerId.value = id;
  }

  const sourceParam = ref<string>();
  function setSourceParam(value: string) {
    sourceParam.value = value;
  }

  // MARK: Profile
  const profileData = ref<IProfileData>();

  function getUserId() {
    const user: any = useTypedNuxtApp().$firebase.auth?.currentUser;
    if (!user) return null;
    return `${user.uid}`;
  }

  async function getUserProfile() {
    const { $authApi, $firebase, $datadogRum } = useTypedNuxtApp();
    const user = $firebase.auth?.currentUser;

    try {
      if (!user) {
        throw new Error('getUserProfile: user is not logged in Firebase');
      }

      const sessionKey = await getIdToken();
      const data = await $authApi.post<Omit<IProfileResponse, 'haserror'>>('/getPlayerInfo', { sessionKey });

      let state: null | IProfileData['state'];

      if (!data.profile.state) {
        state = null;
      } else if (typeof data.profile.state === 'string') {
        try {
          state = JSON.parse(data.profile.state);
        } catch {
          state = null;
        }
      } else {
        state = data.profile.state;
      }

      const result = {
        ...data,
        profile: {
          ...data.profile,
          state,
        },
      };

      if (result.status === 'SUCCESS' && result.profile) {
        profileData.value = result.profile;
        $datadogRum?.setUser(profileData.value);
      } else {
        throw new Error('Error getting user profile');
      }
    } catch (error) {
      console.warn(error);
    }
  }

  function getUser() {
    const auth = useTypedNuxtApp().$firebase.auth;
    return auth?.currentUser;
  }

  // MARK: Currency
  const activeCurrency = ref<TUserCurrency>(EUserCurrency.Dollars);
  const activeBankId = computed(() => {
    return activeCurrency.value === EUserCurrency.Dollars ? EBankId.Dollars : EBankId.PlayMoney;
  });

  // MARK: PWA
  const isPwaBannerEnabled = ref(false);
  const { isPortraitMobile, windowWidth } = useDeviceType();
  const isPwaInstallBannerVisible = computed(() => {
    if (!isPortraitMobile.value) return false;
    if (windowWidth.value >= 1024) return false;
    return isPwaBannerEnabled.value;
  });

  function setIsPwaBannerEnabled(value: boolean) {
    isPwaBannerEnabled.value = value;
  }

  // MARK: Balance
  const totalBalance = ref<number>(0);
  const depositBalance = ref<number>(0);
  const withdrawableBalance = ref<number>(0);
  const playMoneyBalance = ref<number>(0);

  const formattedBalance = computed(() => `$ ${formatBalance(totalBalance.value)}`);
  const formattedPmBalance = computed(() => `${formatBalance(playMoneyBalance.value)} PM`);

  watchEffect(() => {
    totalBalance.value = depositBalance.value + withdrawableBalance.value;
  });

  function setBalances(data: IBalanceData) {
    depositBalance.value = data.deposit;
    withdrawableBalance.value = data.withdrawable;
    playMoneyBalance.value = data.pm;
  }

  // MARK: General auth
  async function setLoggedIn(isAutoSignIn = false) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await getIdToken();

      isLoggedIn.value = true;
      loggedInState.value = true;

      const data = await $authApi.post<ISetLoggedInResponse>('/setLoggedIn', { isAutoSignIn, sessionKey });

      if (data.status !== 'SUCCESS' && !data.session) {
        throw new Error('Error getting user session');
      }

      const session = useCookie(COOKIE_SESSION);
      session.value = data.session;

      if (route.path === '/') {
        navigateTo({ name: ERouteName.LOBBY });
      }

      stores.modals.close('sign-in');
    } catch (error: unknown) {
      console.warn('setLoggedIn error:');
      console.warn(error);
    }
  }

  async function logoutAndRedirect() {
    await signOutOfFirebase();
    idTokenCookie.value = null;
    navigateTo({ name: ERouteName.LOGGED_OUT });
  }

  async function signOutOfFirebase() {
    const auth = useTypedNuxtApp().$firebase.auth;
    await auth?.signOut();
  }

  async function logUserOut() {
    try {
      const session = useCookie(COOKIE_SESSION);
      session.value = null;
    } catch (e) {
      console.error(e);
    } finally {
      isLoggedIn.value = false;
      loggedInState.value = false;
      idTokenCookie.value = null;
    }
  }

  async function processUserLogin() {
    try {
      isCurrentUserDataFetching.value = true;

      if (!isLoggedIn.value) {
        await setLoggedIn(true);
      }

      if (!isLoggedIn.value) {
        return;
      }

      await getUserProfile();

      if (!profileData.value) {
        return;
      }

      // these 2 blocks for closed and banned account most likely are useless due to 401 error handling
      if (profileData.value.status === 'closed' || profileData.value.accountStatus === 'Disabled') {
        stores.modals.open('closed-account', LazyModalGeneric, {
          title: 'Your account is closed.',
          text: `
            <div class="flex flex-col">
                <div>Your account is temporarily closed.</div>
                <div>Please contact <a href='/contact-us' class='text-primary'>Customer Support</a>.</div>
            </div>
          `,
          actions: [
            {
              label: 'OK',
              color: 'primary',
              onClick: () => navigateTo({ name: ERouteName.CONTACT_US }),
            },
          ],
        });
        return stores.signUp.handleSignOut();
      }

      if (profileData.value.disabled) {
        stores.modals.open('banned-account', LazyModalGeneric, {
          title: 'Your account is banned.',
          text: `
            <div class="flex flex-col">
                <div>Your account is permanently banned.</div>
                <div>Please contact <a href='/contact-us' class='text-primary'>Customer Support</a>.</div>
            </div>
          `,
          actions: [
            {
              label: 'OK',
              color: 'primary',
              onClick: () => navigateTo({ name: ERouteName.CONTACT_US }),
            },
          ],
        });
        return stores.signUp.handleSignOut();
      }

      stores.games.getGamesList();
      loadCashier();

      stores.firebase.setupAlertSystemListeners();

      setTimeout(() => {
        stores.firebase.setupLastPlayedUsersListeners();

        loadTournamentsFeature();
        loadWelcomeFeature();
        loadVipFeature();
      }, 0);
    } catch (error) {
      console.error(error);
    } finally {
      isCurrentUserDataFetching.value = false;
    }
  }

  function loadTournamentsFeature() {
    const tournamentsLoader = useTournamentsLoader({ autoLoad: false });
    tournamentsLoader.load().then((store) => {
      store?.getAvailableTournaments();
      store?.getUserAvailableReward();
    });
  }

  function loadCashier() {
    const cashierLoader = useCashierLoader({ autoLoad: false });
    cashierLoader.load().then((store) => store?.getCurrencies());
  }

  function loadVipFeature() {
    const vipFeature = useVipFeature({ autoLoad: false });
    vipFeature.load().then((store) => store?.getVipStatus());
  }

  function loadWelcomeFeature() {
    const welcomeFeature = useWelcomeFeature({ autoLoad: false });
    welcomeFeature.load().then((store) => {
      store?.getWelcomeBonusSpots();
      store?.getWelcomeBonus();
    });
  }

  function clearBalances() {
    totalBalance.value = 0;
    depositBalance.value = 0;
    withdrawableBalance.value = 0;
    playMoneyBalance.value = 0;
  }

  function clearUser() {
    profileData.value = undefined;
    clearBalances();
    logUserOut();
  }

  async function checkUserNameAvailable(displayName: string) {
    const { $authApi } = useTypedNuxtApp();
    let isNameAvailable = false;

    try {
      const data = await $authApi.post<IUserNameResponse>('/displayNameExist', { displayName });

      if (!data) {
        throw new Error('checkUserNameAvailable failed');
      }

      isNameAvailable = !data.hasError;
    } catch (error) {
      const errorText = error.response?._data?.message || error.message || 'Unknown error';
      console.error(errorText);
      showErrorToast('Failed to check username', errorText);
    }

    return isNameAvailable;
  }

  async function checkEmailAvailable(email: string) {
    const { $authApi } = useTypedNuxtApp();
    let emailIsAvailable = false;

    try {
      const data = await $authApi.post<IEmailAvailableResponse>('/emailExist', { email });

      if (!data) {
        throw new Error('checkEmailAvailable failed');
      }

      emailIsAvailable = !data.hasError;
    } catch (error) {
      console.error(error);
    }

    return emailIsAvailable;
  }

  async function checkEmailVerified(email: string) {
    const { $authApi } = useTypedNuxtApp();
    let isEmailVerified = false;

    try {
      const data = await $authApi.post<ICheckEmailVerifiedResponse>('/isEmailVerified', { email });
      isEmailVerified = data?.emailVerified || false;
    } catch (error) {
      console.error(error);
    }

    return isEmailVerified;
  }

  async function setEmailVerified(email: string) {
    const { $authApi } = useTypedNuxtApp();
    let isEmailVerified = false;

    try {
      const data = await $authApi.post<IGenericAPIResponse>('/setEmailVerified', { email });
      isEmailVerified = data.status === 'SUCCESS';
    } catch (error) {
      console.error(error);
    }

    return isEmailVerified;
  }

  async function sendPasswordResetEmail(email: string) {
    const { $authApi } = useTypedNuxtApp();

    let response: {
      haserror: boolean;
      title: string;
      message: string;
    };

    try {
      const data = await $authApi.post<IPasswordResetResponse>('/resetPassword', { email });

      response = {
        haserror: data.haserror,
        message: data.message,
        title: '',
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        response = {
          haserror: true,
          title: 'Email Not Found',
          message: 'The provided email address is not associated with any account. Please try again.',
        };
      } else {
        response = {
          haserror: true,
          title: 'Something went wrong',
          message: error.response?.data?.message || 'Failed to receive data from an external API',
        };
      }
    }

    return response;
  }

  async function createUser(createUserData: ICreateUserData) {
    const { $authApi } = useTypedNuxtApp();
    let success = false;

    const postData = structuredClone({
      ...createUserData,
      ...loadingQueryParams.getUTMParams(),
    });

    if (partnerId.value) {
      postData.partnerId = partnerId.value;
    }

    if (sourceParam.value) {
      postData.source = sourceParam.value;
    }

    if (!postData.displayName) {
      delete postData.displayName;
    }

    try {
      const data = await $authApi.post<ICreateUserResponse>('/createUser', postData);

      if (!data || data.haserror) {
        throw new Error('Create user failed');
      }

      profileData.value = data.user;

      success = true;
    } catch (error) {
      console.warn(error);
    } finally {
      loadingQueryParams.removeUTMParamsFromLocalStorage();
    }

    return success;
  }

  async function updateUserProfile(updateData: Partial<IProfileData>) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await getIdToken();
      const { data, status, haserror } = await $authApi.post<IUpdateUserProfileResponse>('/updateUserProfile', {
        sessionKey,
        ...updateData,
      });

      if (haserror) {
        throw new Error('Update user profile failed');
      }

      const processedResponse = {
        status,
        haserror,
        profile: {
          ...data,
          state: data?.state ? JSON.parse(data.state) : null,
        },
      } as IProfileResponse;

      profileData.value = processedResponse.profile;

      return processedResponse;
    } catch (error) {
      console.error(error);

      if (error) {
        return error.data || error;
      }

      return {
        haserror: true,
        status: 'Update user profile failed',
      };
    }
  }

  async function resendVerificationCode() {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await getIdToken();
      const data = await $authApi.post<IResendVerificationResponse>('/resend-verification-code', { sessionKey });

      if (data.haserror) {
        return {
          success: false,
          message: data.message,
        };
      } else {
        return {
          success: true,
          message: 'Check your mail, please!',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error?.data?.message || 'Failed to receive data from an external API',
      };
    }
  }

  async function verifyEmailByCode(code: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await getIdToken();
      const data = await $authApi.post<{ haserror?: boolean; message: string }>('/verify-email', {
        code,
        sessionKey,
      });

      return {
        success: !data.haserror,
        message: data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error?.data?.status || 'Failed to receive data from an external API',
      };
    }
  }

  // MARK: WebSocket
  async function initWebsocket(options: ISocketOptions = {}) {
    const { $socket } = useTypedNuxtApp();
    // Socket.io-client is lazy loaded here (after login) to reduce initial bundle
    await $socket.init({ ...options }, onSocketInit);
  }

  function destroyWebsocket() {
    const { $socket } = useTypedNuxtApp();
    $socket.disconnect();
  }

  const onSocketOnlineEvent = (data: IOnlineEventData) => {
    const loader = useFriendsStoreLoader({ autoLoad: false });
    loader.load().then((store) => store?.onSocketOnlineEvent(data));
  };

  const onFriendChanged = (friendData: IFriendData) => {
    const loader = useFriendsStoreLoader({ autoLoad: false });
    loader.load().then((store) => store?.onSocketFriendChanged(friendData));
  };

  const onFriendRemoved = (friendData: IFriendData) => {
    const loader = useFriendsStoreLoader({ autoLoad: false });
    loader.load().then((store) => store?.onSocketFriendRemoved(friendData));
  };

  async function onSocketInit() {
    const { $socket, $firebase } = useTypedNuxtApp();
    const user = $firebase.auth?.currentUser;

    if (!user) return;

    const sessionKey = await getIdToken();

    $socket.off('balance');
    $socket.off('online');
    $socket.off('profile');
    $socket.off('bonus');
    $socket.off('friend-added');
    $socket.off('friend-changed');
    $socket.off('friend-removed');
    $socket.off('vip');

    $socket.on('balance', onSocketBalanceChanged);
    $socket.on('profile', onSocketProfileEvent);
    $socket.on('bonus', onSocketBonusEvent);
    $socket.on('vip', onSocketVipEvent);
    $socket.on('online', onSocketOnlineEvent);
    $socket.on('friend-added', onFriendChanged);
    $socket.on('friend-changed', onFriendChanged);
    $socket.on('friend-removed', onFriendRemoved);

    $socket.emit('auth', { sessionKey });
  }

  function onSocketBalanceChanged(dataJSON: string) {
    const user = useTypedNuxtApp().$firebase.auth?.currentUser;
    if (!user) return;

    const { $socket } = useTypedNuxtApp();
    const debug = $socket.isDebugEnabled();

    try {
      const parsedData = JSON.parse(dataJSON) as TJsonBalanceData;

      if (!parsedData) {
        debug && console.error(`WebSocket: incoming data is not valid: ${dataJSON}`);
        return;
      }

      const balance: IBalanceData = {
        deposit: Number.parseInt(parsedData.deposit, 10) || 0,
        withdrawable: Number.parseInt(parsedData.withdrawable, 10) || 0,
        pm: Number.parseInt(parsedData.pm, 10) || 0,
      };

      setBalances(balance);
    } catch (error) {
      console.error(`WebSocket: failed to parse incoming data: ${dataJSON}`);
      console.error(`WebSocket: detailed error - ${error}`);
    }
  }

  async function onSocketProfileEvent(data: { emailVerified: boolean; phoneVerified: boolean }) {
    const { callHook } = useTypedNuxtApp();
    const { emailVerified, phoneVerified } = data;

    if (emailVerified) {
      await callHook(EAppCustomHooks.ProfileEmailVerified);
    } else if (phoneVerified) {
      await callHook(EAppCustomHooks.ProfileEmailVerified);
    }
  }

  async function onSocketBonusEvent(message: TWelcomeBonusMessage) {
    const { store, load } = useWelcomeFeature();
    if (!store?.value) await load();
    store.value.syncWelcomeBonus(message);
  }

  async function onSocketVipEvent(message: TVipSocketMessage) {
    const { store, load } = useVipFeature();
    if (!store?.value) await load();
    store.value.processVipSocketEvent(message);
  }

  // MARK: KYC and purchases
  const isKycApproved = computed(() => {
    const user = profileData.value;
    const userKycCompleted = user?.kycStatus === EKycStatus.COMPLETED;
    const userKycGreen = user?.kycResult === EKycResult.GREEN;
    return userKycCompleted && userKycGreen;
  });
  const isKycCompleted = computed(() => profileData.value?.kyc?.status === EKycStatus.COMPLETED);
  const isAccountVerified = computed(() => isKycCompleted.value && isKycApproved.value);

  function setKycCompleted() {
    if (!profileData.value) return;

    profileData.value.kycStatus = EKycStatus.COMPLETED;
    profileData.value.kycResult = EKycResult.GREEN;
    profileData.value.kyc = { result: EKycResult.GREEN, status: EKycStatus.COMPLETED };
  }

  async function getSumsubAccessToken() {
    const { $authApi, $firebase } = useTypedNuxtApp();
    const user = $firebase.auth?.currentUser;

    try {
      if (!user) {
        throw new Error('setLoggedIn: user is not logged in Firebase');
      }

      const sessionKey = await getIdToken();
      const data = await $authApi.post<{ token?: string }>('/kyc/getAccessToken', { sessionKey });
      return data.token || '';
    } catch (error) {
      console.error(`Error getting Sumsub access token: ${error}`);
      return '';
    }
  }

  async function claimBonuses(bonusIds: string[]) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await getIdToken();
      const { hasError } = await $authApi.post<IClaimBonusesResponse>('/bonuses/claim', { sessionKey, ids: bonusIds });

      if (hasError) {
        throw new Error('Failed to claim bonuses');
      }

      return true;
    } catch (error: any) {
      console.error(error);
      showErrorToast('Failed to claim bonuses', error.response?.data?.message || 'Unknown error');
      return false;
    }
  }

  return {
    getUser,

    totalBalance,
    depositBalance,
    withdrawableBalance,
    playMoneyBalance,
    formattedPmBalance,
    formattedBalance,
    activeCurrency,
    activeBankId,
    profileData,
    getUserId,
    partnerId,
    sourceParam,
    isLoggedIn,
    socialSignUpIsPending,
    socialSignInIsPending,
    setKycCompleted,
    isKycCompleted,
    isKycApproved,
    isAccountVerified,
    getIdToken,

    isCurrentUserDataFetching,
    isPwaBannerEnabled,
    isPwaInstallBannerVisible,

    continueLoggingInUser,
    logUserOut,
    verifyEmailByCode,
    clearUser,
    signOutOfFirebase,
    logoutAndRedirect,
    checkUserNameAvailable,
    checkEmailAvailable,
    checkEmailVerified,
    setEmailVerified,
    createUser,
    resendVerificationCode,
    setSocialSignUpIsPending,
    setSocialSignInIsPending,
    sendPasswordResetEmail,
    updateUserProfile,
    getSumsubAccessToken,
    setIsPwaBannerEnabled,

    claimBonuses,
  };
});
